import { useCallback, useEffect, useRef, useState } from 'react';

import { parseSkillMarkdown } from '@aline/common';
import { link } from '@aline/routes';
import type { Reference } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Apollo } from 'src/gql/generated';

import type { EditableSkillScope } from './skill-access-menu';
import { getSkillContentError, getSkillContentWithFinalName } from './skill-editor-content';
import { getErrorMessage, getOutdatedSkillError, SKILLS_LIST_REFETCH } from './skill-editor-errors';
import { getRunFocusTarget } from './skill-editor-status';
import { getIsOutdatedSkillVersionError } from './skill-save-error';
import { INSTRUCTIONS_TREE_ITEM } from './skill-tree-ids';
import type { SkillTreeItem } from './skill-tree-ids';
import { useEditSkillWithAi } from './use-edit-skill-with-ai';
import { useSkillDraftPublishing } from './use-skill-draft-publishing';
import type { PendingAction, SkillDraftSession } from './use-skill-draft-session';

/**
 * The toolbar's actions. Run, Back, Duplicate and Publish wait for the save of everything
 * typed before the click, then run once it lands.
 */
export function useSkillEditorActions({
  focusField,
  session,
  setSection,
}: {
  /** Focus the field with this aria label once it renders. */
  focusField: (label: string) => void;
  session: SkillDraftSession;
  setSection: React.Dispatch<React.SetStateAction<SkillTreeItem>>;
}) {
  const {
    canEdit,
    clearRetryableSaveError,
    contentError,
    contentRef,
    deletingRef,
    dirty,
    flushEditors,
    getHasUnsavedChanges,
    pendingAction,
    reportSaveError,
    requestSave,
    saveError,
    saveErrorRef,
    saveQueue,
    saving,
    setPendingAction,
    skill,
    skillRef,
    writeSaveError,
    writeSkill,
  } = session;
  const navigate = useNavigate();
  // Set while a scope change waits for a running save, so a second click does not also send.
  const scopeChangeRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const publishing = useSkillDraftPublishing({ session });
  const { performPublish, performTest } = publishing;
  const { editSkillWithAi } = useEditSkillWithAi({
    getSkill: () => skillRef.current,
    resolvePinnedSkill: publishing.resolvePinnedSkill,
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Once Run was pressed, a missing name or description shows as an error.
  const [runAttempted, setRunAttempted] = useState(false);
  const [scopePending, setScopePending] = useState(false);
  const [setAgentSkillScope, { loading: changingScope }] = Apollo.useSetAgentSkillScopeMutation({
    errorPolicy: 'none',
  });
  const [deleteAgentSkill, { loading: deleting }] = Apollo.useDeleteAgentSkillMutation({
    errorPolicy: 'none',
  });
  const [duplicateSkill, { loading: duplicating }] = Apollo.useDuplicateAgentSkillMutation({
    errorPolicy: 'none',
  });

  /** Ask for an action that runs once everything typed is saved. */
  const queueAction = useCallback(
    (action: PendingAction) => {
      flushEditors();
      clearRetryableSaveError();
      setPendingAction(action);
    },
    [clearRetryableSaveError, flushEditors, setPendingAction]
  );

  const openDelete = useCallback(() => setDeleteOpen(true), []);
  const closeDelete = useCallback(() => setDeleteOpen(false), []);
  const deleteSkill = useCallback(async () => {
    if (!skill || !canEdit || deleting || deletingRef.current) return;

    try {
      /**
       * A save still running would land on a deleted skill and report that the last changes
       * were lost. It finishes first, and no save starts after it.
       */
      deletingRef.current = true;
      if (saveQueue.getIsRunning()) await saveQueue.request();
      await deleteAgentSkill({
        // The list and the chat picker render from the cache first, so drop the skill from it.
        update: cache => {
          cache.modify({
            fields: {
              agentSkills: (existing: readonly Reference[] = [], { readField }) =>
                existing.filter(ref => readField<string>('id', ref) !== skill.id),
            },
          });
        },
        variables: { id: skill.id },
      });
      enqueueSnackbar('Skill deleted.', { variant: 'success' });
      navigate(link.AdminAgentSkillIndex());
    } catch {
      deletingRef.current = false;
      enqueueSnackbar('Could not delete the skill. Please try again.', { variant: 'error' });
    }
  }, [
    canEdit,
    deleteAgentSkill,
    deleting,
    deletingRef,
    enqueueSnackbar,
    navigate,
    saveQueue,
    skill,
  ]);

  const performDuplicate = useCallback(async () => {
    if (!skill) return;

    try {
      const result = await duplicateSkill({
        refetchQueries: [Apollo.GetAgentSkillsDocument],
        variables: { id: skill.id },
      });
      if (!result.data) return;

      enqueueSnackbar('Skill duplicated.', { variant: 'success' });
      navigate(
        link.AdminAgentSkillDetail({ skillId: result.data.duplicateAgentSkill.agentSkill.id })
      );
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Could not duplicate this skill.'), {
        variant: 'error',
      });
    }
  }, [duplicateSkill, enqueueSnackbar, navigate, skill]);

  const handleDuplicate = useCallback(() => {
    // Nothing of a viewer's, or of a conflicted editor's, can be saved first.
    if (!canEdit || saveErrorRef.current?.outdated) void performDuplicate();
    else queueAction('duplicate');
  }, [canEdit, performDuplicate, queueAction, saveErrorRef]);

  const handlePublish = useCallback(() => queueAction('publish'), [queueAction]);
  const handleEditWithAi = useCallback(() => queueAction('editWithAi'), [queueAction]);

  const handleRun = useCallback(() => {
    flushEditors();
    const latestContent = getSkillContentWithFinalName(contentRef.current);
    setRunAttempted(true);
    const focus = getRunFocusTarget({
      contentError: getSkillContentError(latestContent),
      parsed: parseSkillMarkdown(latestContent),
    });
    if (focus) {
      setSection(INSTRUCTIONS_TREE_ITEM);
      focusField(focus);
      return;
    }
    queueAction('run');
  }, [contentRef, flushEditors, focusField, queueAction, setSection]);

  const handleBack = useCallback(() => {
    if (!canEdit || saveErrorRef.current?.outdated) navigate(link.AdminAgentSkillIndex());
    else queueAction('back');
  }, [canEdit, navigate, queueAction, saveErrorRef]);

  // Run the waiting action once the save of everything typed before it has landed.
  useEffect(() => {
    if (!pendingAction || saving || saveError) return;
    if (contentError && dirty) {
      writeSaveError({ message: contentError, outdated: false });
      setPendingAction(null);
      return;
    }
    flushEditors();
    if (dirty || getHasUnsavedChanges()) return;
    setPendingAction(null);
    switch (pendingAction) {
      case 'back':
        navigate(link.AdminAgentSkillIndex());
        return;
      case 'duplicate':
        void performDuplicate();
        return;
      case 'editWithAi':
        void editSkillWithAi();
        return;
      case 'publish':
        void performPublish();
        return;
      case 'run':
        void performTest();
    }
  }, [
    contentError,
    dirty,
    editSkillWithAi,
    flushEditors,
    getHasUnsavedChanges,
    navigate,
    pendingAction,
    performDuplicate,
    performPublish,
    performTest,
    saveError,
    saving,
    setPendingAction,
    writeSaveError,
  ]);

  const changeScope = useCallback(
    async (scope: EditableSkillScope) => {
      if (
        !skillRef.current ||
        scopeChangeRef.current ||
        !canEdit ||
        deleting ||
        saveErrorRef.current?.outdated ||
        scope === skillRef.current.scope
      )
        return;
      scopeChangeRef.current = true;
      setScopePending(true);
      try {
        flushEditors();
        clearRetryableSaveError();
        await requestSave();
        const saved = skillRef.current;
        if (saveErrorRef.current || getHasUnsavedChanges()) return;
        const result = await setAgentSkillScope({
          refetchQueries: SKILLS_LIST_REFETCH,
          variables: {
            input: {
              expectedVersionId: saved.currentVersion?.id ?? null,
              id: saved.id,
              scope,
            },
          },
        });
        if (!result.data) throw new Error('The server did not confirm the access change.');
        writeSkill({ ...skillRef.current, scope: result.data.setAgentSkillScope.agentSkill.scope });
      } catch (error) {
        if (getIsOutdatedSkillVersionError(error)) reportSaveError(getOutdatedSkillError('share'));
        else
          enqueueSnackbar('Could not change skill access. Please try again.', { variant: 'error' });
      } finally {
        scopeChangeRef.current = false;
        setScopePending(false);
      }
    },
    [
      canEdit,
      clearRetryableSaveError,
      deleting,
      enqueueSnackbar,
      flushEditors,
      getHasUnsavedChanges,
      reportSaveError,
      requestSave,
      saveErrorRef,
      setAgentSkillScope,
      skillRef,
      writeSkill,
    ]
  );

  return {
    ...publishing,
    changeScope,
    changingScope,
    closeDelete,
    deleteOpen,
    deleteSkill,
    deleting,
    duplicating,
    handleBack,
    handleDuplicate,
    handleEditWithAi,
    handlePublish,
    handleRun,
    openDelete,
    runAttempted,
    scopePending,
  };
}
